// frontend/src/features/production/serialization/pages/LotAffectedUnitsPage.tsx
//
// ADR-104 Rule SN-11 / Recall scenario — given a contaminated/problematic
// pallet, show every serial-tracked unit that came from it, directly or
// transitively through the genealogy graph. Known perf caveat (OQ-SN-02,
// still open in ADR-104): deep un-frozen graphs may be slow — see backend
// README for the Closure Table follow-up note.

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAffectedUnits } from "../api/serializationApi";
import { useV1InventoryLotsList } from "@/api/generated/v1/v1";
import { PageBanner } from "@/shared/components/PageBanner";

export function LotAffectedUnitsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [lotId, setLotId] = useState(searchParams.get("lot_id") ?? "");
  const [submittedLotId, setSubmittedLotId] = useState<string | undefined>(
    searchParams.get("lot_id") ?? undefined
  );

  const { data: lotsData } = useV1InventoryLotsList({ page_size: 1000 } as any);
  const { data, isLoading, isError } = useAffectedUnits(submittedLotId);
  const results = (data as any)?.results ?? [];

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }} dir="rtl">
      <PageBanner
        title="محصولات متأثر از یک پالت (Recall)"
        subtitle="بررسی فراخوانی (Recall) و مشاهده قطعاتی که از یک پالت/لات معیوب استفاده کرده‌اند"
        icon="🔙"
        helpLink="/help/production/serialization/lot-affected-units"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "640px", margin: "0 auto", padding: "2rem", width: "100%" }}>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
          <select
            value={lotId}
            onChange={(e) => setLotId(e.target.value)}
            dir="ltr"
            className="form-input-control"
            style={{ flex: 1, padding: "0.6rem 0.9rem", borderRadius: "8px", border: "1px solid var(--color-border)", background: "#fff" }}
          >
            <option value="">انتخاب پالت (InventoryLot)...</option>
            {lotsData?.results?.map((lot: any) => (
              <option key={lot.id} value={lot.id}>
                {lot.lot_number}
              </option>
            ))}
          </select>
          <button 
            onClick={() => setSubmittedLotId(lotId || undefined)}
            style={{
              padding: "0.6rem 1.25rem",
              borderRadius: "8px",
              background: "#007270",
              color: "#ffffff",
              fontWeight: 700,
              border: "none",
              cursor: "pointer"
            }}
          >
            جست‌وجو
          </button>
        </div>

        {isLoading && <p>در حال جست‌وجو…</p>}
        {isError && (
          <p style={{ color: "#b91c1c", fontSize: "0.85rem", padding: "1rem", background: "#fef2f2", borderRadius: "8px" }}>
            خطا در دریافت اطلاعات — پالت یافت نشد یا مشکلی پیش آمد.
          </p>
        )}

        {submittedLotId && !isLoading && !isError && (
          <div>
            <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "1rem" }}>
              {results.length} واحد سریال‌دار متأثر از این پالت پیدا شد.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {results.map((unit: any) => (
                <div
                  key={unit.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "1rem",
                    borderRadius: "10px",
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid var(--glass-border)",
                  }}
                >
                  <strong dir="ltr" style={{ color: "var(--color-text-main)", fontSize: "1.05rem" }}>{unit.serial_number}</strong>
                  <button
                    onClick={() =>
                      navigate(`/production/serialization/trace?serial_number=${unit.serial_number}`)
                    }
                    style={{
                      padding: "0.4rem 0.8rem",
                      borderRadius: "6px",
                      background: "rgba(0, 114, 112, 0.1)",
                      color: "#007270",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      border: "none",
                      cursor: "pointer"
                    }}
                  >
                    مشاهدهٔ ردیابی
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
